import { useState } from 'react';
import { calmTokens as t } from '../../../shared/tokens';
import { labelService } from '../../../services/api-secure';
import { fmtEUR } from '../../../shared/format';
import { IconSparkle } from '../../../shared/icons';
import { RULE_ORIGIN_KINDS } from '../../../shared/labels';
import CsvDropZone from './CsvDropZone';
import { Chip, PillButton } from './ui';
import { monoStyle } from './styles';

const chk = { width: 15, height: 15, accentColor: '#a5b4fc', cursor: 'pointer' };

/**
 * Apprentissage depuis un export couvrant des mois déjà saisis :
 * upload → propositions cochées → ambiguïtés à trancher → enregistrement.
 */
export default function LearnFlow({ onFeedback, onRulesChanged }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [choices, setChoices] = useState({});

  const analyze = async (f) => {
    setFile(f);
    setBusy(true);
    setResult(null);
    try {
      const r = await labelService.learn(f);
      setResult(r);
      setChecked(new Set(r.proposals.map((p) => p.match)));
      setChoices({});
      if (!r.proposals.length && !r.ambiguities.length) {
        onFeedback(
          r.pairs
            ? 'Aucune nouvelle règle à proposer : tes libellés sont déjà connus.'
            : 'Aucune opération de ce fichier ne correspond à une saisie manuelle.',
          'info'
        );
      }
    } catch (error) {
      console.error('Learn error:', error);
      onFeedback(error.message || 'Apprentissage impossible.', 'error');
      setFile(null);
    }
    setBusy(false);
  };

  const save = async () => {
    const rules = [
      ...result.proposals.filter((p) => checked.has(p.match)).map((p) => ({ match: p.match, matchType: 'exact', label: p.label, emoji: p.emoji, origin: 'learned' })),
      ...result.ambiguities
        .filter((a) => choices[a.match])
        .map((a) => {
          const opt = a.options.find((o) => `${o.emoji}|${o.label}` === choices[a.match]);
          return { match: a.match, matchType: 'exact', label: opt.label, emoji: opt.emoji, origin: 'user' };
        }),
    ];
    if (!rules.length) {
      onFeedback('Aucune règle sélectionnée.', 'info');
      return;
    }
    setBusy(true);
    try {
      const r = await labelService.createRules(rules);
      onFeedback(`${r.created + r.updated} règle(s) enregistrée(s)${r.skipped ? `, ${r.skipped} ignorée(s)` : ''}.`, 'success');
      setResult(null);
      setFile(null);
      onRulesChanged?.();
    } catch (error) {
      console.error('Save rules error:', error);
      onFeedback(error.message || 'Enregistrement impossible.', 'error');
    }
    setBusy(false);
  };

  return (
    <div>
      <CsvDropZone
        onFile={analyze}
        busy={busy}
        file={file}
        disabled={busy}
        title="Déposer un export couvrant des mois déjà saisis"
        hint="Les opérations sont rapprochées de tes saisies pour deviner tes libellés · 2 Mo max"
      />

      {result && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: t.textDim, marginBottom: 12 }}>
            {result.pairs} opération{result.pairs > 1 ? 's' : ''} rapprochée{result.pairs > 1 ? 's' : ''} sur {result.totalOps}
            {result.skippedUserRules ? ` · ${result.skippedUserRules} déjà couverte(s) par tes règles` : ''}. Les exemples ont été mémorisés pour le classifieur.
          </div>

          {result.proposals.length > 0 && (
            <div style={{ border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden', background: t.bg, marginBottom: 12 }}>
              <div style={{ padding: '8px 16px', fontSize: 10.5, color: t.textMute, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: `1px solid ${t.border}` }}>
                Règles proposées
              </div>
              {result.proposals.map((p) => (
                <label
                  key={p.match}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderTop: `1px solid ${t.border}`, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    style={chk}
                    checked={checked.has(p.match)}
                    onChange={(e) => {
                      const next = new Set(checked);
                      if (e.target.checked) next.add(p.match); else next.delete(p.match);
                      setChecked(next);
                    }}
                  />
                  <span style={{ fontSize: 12, ...monoStyle, minWidth: 0, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.bankLabel}>
                    {p.match}
                  </span>
                  <span style={{ fontSize: 12, color: t.textMute }}>→</span>
                  <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{p.emoji ? `${p.emoji} ` : ''}{p.label}</span>
                  <span style={{ fontSize: 11, color: t.textMute, ...monoStyle }}>
                    {p.count}× · {p.sampleAmounts.slice(0, 2).map((a) => fmtEUR(a)).join(', ')}
                  </span>
                  {p.alternatives?.length > 0 && (
                    <span
                      style={{ fontSize: 10.5, color: t.textMute }}
                      title={p.alternatives.map((a) => `${a.emoji ? a.emoji + ' ' : ''}${a.label} ${a.count}×`).join(' · ')}
                    >
                      +{p.alternatives.length} variante{p.alternatives.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {p.existingRule && (
                    <Chip kind={RULE_ORIGIN_KINDS[p.existingRule.origin]} title={`Règle actuelle : ${p.existingRule.emoji || ''} ${p.existingRule.label}`}>
                      remplace
                    </Chip>
                  )}
                </label>
              ))}
            </div>
          )}

          {result.ambiguities.length > 0 && (
            <div style={{ border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden', background: t.bg, marginBottom: 12 }}>
              <div style={{ padding: '8px 16px', fontSize: 10.5, color: t.warn, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: `1px solid ${t.border}` }}>
                À trancher · un même libellé banque, plusieurs libellés chez toi
              </div>
              {result.ambiguities.map((a) => (
                <div key={a.match} style={{ padding: '10px 16px', borderTop: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 12, ...monoStyle, marginBottom: 6 }} title={a.bankLabel}>{a.match}</div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {a.options.map((o) => {
                      const v = `${o.emoji}|${o.label}`;
                      return (
                        <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input type="radio" name={`amb-${a.match}`} style={chk} checked={choices[a.match] === v} onChange={() => setChoices((c) => ({ ...c, [a.match]: v }))} />
                          <span>{o.emoji ? `${o.emoji} ` : ''}{o.label}</span>
                          <span style={{ fontSize: 11, color: t.textMute, ...monoStyle }}>{o.count}×</span>
                        </label>
                      );
                    })}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: t.textMute, cursor: 'pointer' }}>
                      <input type="radio" name={`amb-${a.match}`} style={chk} checked={!choices[a.match]} onChange={() => setChoices((c) => ({ ...c, [a.match]: '' }))} />
                      laisser l’IA décider
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(result.proposals.length > 0 || result.ambiguities.length > 0) && (
            <div style={{ display: 'flex', gap: 8 }}>
              <PillButton primary icon={<IconSparkle size={13} />} onClick={save} disabled={busy}>
                Enregistrer les règles
              </PillButton>
              <PillButton onClick={() => { setResult(null); setFile(null); }} disabled={busy}>
                Ignorer
              </PillButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
